import { Maximize2 } from "lucide-react";
import { CSSProperties, ReactNode, useState } from "react";
import { Link } from "react-router";

import { StatusPill } from "nazare-ui/components";
import { PageSection, PageSectionAction, PageSectionBadge } from "nazare-ui/components/page-section";

import { ScrollShadow } from "../../../scroll-shadow";
import { SkeletonBlock } from "../../../skeleton-block";
import { useScrollEdges } from "../../../use-scroll-edges";
import {
  useActiveWorkflows,
  useActiveWorkflowsStream,
  useWorkflowDefinitions,
  useWorkflowSchedules,
  type WorkflowDefinition,
  type ScheduleSummary,
} from "../../../workflows-api";
import styles from "./summary-cards.module.css";
import { WorkflowRunModal } from "./workflow-run-modal";
import { StatusChip } from "./pages/run-detail-status-chip";
import { workflowRunPauseState } from "../../../workflow-run-control";
import { workflowDisplayLabel } from "./display-labels";

function elapsedSince(iso: string | null): string {
  if (!iso) return "—";
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function relativeTime(iso?: string | null): string {
  if (!iso) return "Next run unavailable";
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (Math.abs(seconds) < 3600) return rtf.format(Math.round(seconds / 60), "minute");
  if (Math.abs(seconds) < 86400) return rtf.format(Math.round(seconds / 3600), "hour");
  return rtf.format(Math.round(seconds / 86400), "day");
}

function scheduleCategory(schedule: ScheduleSummary): "workflow" | "infra" {
  if (schedule.category) return schedule.category;
  return schedule.targets.some((target) => target.kind === "workflow") ? "workflow" : "infra";
}

function SkeletonSummaryRows() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <li key={index} className={styles.row} aria-hidden="true">
          <div className={styles.rowTop}><SkeletonBlock width={`${60 + index * 2}%`} height={13} /></div>
          <div className={styles.rowBottom}><SkeletonBlock width="42%" height={11} /></div>
        </li>
      ))}
    </>
  );
}

function MessageRow({ children }: { children: ReactNode }) {
  return <li className={styles.row}><span className={styles.rowMeta}>{children}</span></li>;
}

function Card({
  title,
  count,
  secondaryCount,
  badges,
  children,
  viewTransitionName,
  maximiseTo,
}: {
  title: string;
  count?: number;
  secondaryCount?: { value: number; label: string };
  badges?: ReactNode;
  children: ReactNode;
  viewTransitionName: string;
  maximiseTo: string;
}) {
  const { ref: listRef, atTop, atBottom } = useScrollEdges<HTMLUListElement>();
  return (
    <PageSection
      className={styles.card}
      style={{ viewTransitionName } as CSSProperties}
      title={title}
      count={count}
      badges={<>
        {badges}
        {secondaryCount && secondaryCount.value > 0 && <PageSectionBadge title="Runs waiting to start">{secondaryCount.value.toLocaleString("en-GB")} {secondaryCount.label}</PageSectionBadge>}
      </>}
      actions={<PageSectionAction asChild aria-label={`Expand ${title}`} title={`Expand ${title}`}>
        <Link to={maximiseTo} viewTransition><Maximize2 aria-hidden="true" /></Link>
      </PageSectionAction>}
    >
      <div className={styles.scrollShadowWrapper}>
        <ul ref={listRef} className={styles.list}>{children}</ul>
        <ScrollShadow atTop={atTop} atBottom={atBottom} />
      </div>
    </PageSection>
  );
}

export function WorkflowSummaryCards() {
  const active = useActiveWorkflows();
  useActiveWorkflowsStream();
  const definitions = useWorkflowDefinitions();
  const schedules = useWorkflowSchedules();
  const [selectedDefinition, setSelectedDefinition] = useState<WorkflowDefinition | null>(null);
  const workflowSchedules = (schedules.data?.schedules ?? []).filter((item) => scheduleCategory(item) === "workflow");
  const infraSchedules = (schedules.data?.schedules ?? []).filter((item) => scheduleCategory(item) === "infra");
  const queuedCount = active.data?.byStatus.queued ?? active.data?.byStatus.pending ?? 0;

  return (
    <div className={styles.grid}>
      <Card
        title="Active runs"
        count={active.data?.total}
        secondaryCount={{ value: queuedCount, label: "queued" }}
        badges={active.data?.activeSteps !== undefined && <PageSectionBadge title="Currently executing workflow steps">{active.data.activeSteps.toLocaleString("en-GB")} active steps</PageSectionBadge>}
        viewTransitionName="wf-card-active"
        maximiseTo="/workflows/active"
      >
        {active.isLoading ? <SkeletonSummaryRows /> : active.error ? <MessageRow>Couldn’t load active runs.</MessageRow> : active.data?.runs.length ? active.data.runs.map((run) => (
          <li key={run.runId} className={`${styles.row} ${styles.activeRunRow}`}>
            <Link className={styles.rowLink} to={`/workflows/runs/${encodeURIComponent(run.runId)}`} aria-label={`View run ${run.runId}`} />
            <div className={styles.rowTop}>
              <span className={styles.statusControl}><StatusChip status={run.status} pauseState={workflowRunPauseState(run)} /></span>
              <span className={styles.rowTitle}>{workflowDisplayLabel(run, definitions.data?.definitions)}</span>
              {run.label && <span className={styles.rowMeta}>{run.label}</span>}
            </div>
            <div className={styles.rowBottom}>
              <span className={styles.rowMono}>{run.runId}</span>
              <span className={styles.rowMeta}>{elapsedSince(run.startedAt ?? run.createdAt)}</span>
            </div>
          </li>
        )) : <MessageRow>No active runs.</MessageRow>}
      </Card>

      <Card title="Workflows" count={definitions.data?.definitions.length} viewTransitionName="wf-card-definitions" maximiseTo="/workflows/definitions">
        {definitions.isLoading ? <SkeletonSummaryRows /> : definitions.error ? <MessageRow>Couldn’t load workflows.</MessageRow> : definitions.data?.definitions.length ? definitions.data.definitions.map((workflow) => (
          <li key={workflow.name} className={`${styles.row} ${styles.workflowRow}`}>
            <span className={styles.workflowText}>
              <span className={styles.workflowName}>{workflow.label}</span>
              <span className={styles.workflowDescription} title={workflow.description}>{workflow.description}</span>
            </span>
            {workflow.allowManual && (
              <button
                type="button"
                className={styles.runButton}
                onClick={() => setSelectedDefinition(workflow)}
              >Run</button>
            )}
          </li>
        )) : <MessageRow>No workflows are registered.</MessageRow>}
      </Card>

      <Card title="Scheduled workflows" count={schedules.data ? workflowSchedules.length : undefined} viewTransitionName="wf-card-scheduled" maximiseTo="/workflows/scheduled">
        {schedules.isLoading ? <SkeletonSummaryRows /> : schedules.error ? <MessageRow>Couldn’t load schedules.</MessageRow> : workflowSchedules.length ? workflowSchedules.map((schedule) => (
          <li key={schedule.name} className={styles.row}>
            <div className={styles.rowTop}>
              <span className={styles.rowTitle}>{schedule.label ?? schedule.name}</span>
              <StatusPill tone={schedule.state === "ENABLED" ? "success" : "neutral"}>{schedule.state === "ENABLED" ? "Enabled" : "Disabled"}</StatusPill>
            </div>
            <div className={styles.rowBottom}><span className={styles.rowMono}>{schedule.scheduleExpression}</span><span className={styles.rowMeta}>{relativeTime(schedule.nextFireTime)}</span></div>
          </li>
        )) : <MessageRow>No workflow schedules.</MessageRow>}
      </Card>

      <Card title="Infra schedules" count={schedules.data ? infraSchedules.length : undefined} viewTransitionName="wf-card-infra" maximiseTo="/workflows/infra">
        {schedules.isLoading ? <SkeletonSummaryRows /> : schedules.error ? <MessageRow>Couldn’t load schedules.</MessageRow> : infraSchedules.length ? infraSchedules.map((schedule) => (
          <li key={schedule.name} className={styles.row}>
            <div className={styles.rowTop}>
              <span className={styles.rowTitle} title={schedule.name}>{schedule.label ?? schedule.name}</span>
              <StatusPill tone={schedule.state === "ENABLED" ? "success" : "neutral"}>{schedule.state === "ENABLED" ? "Enabled" : "Disabled"}</StatusPill>
            </div>
            <div className={styles.rowBottom}><span className={styles.infraType}>{schedule.targets[0]?.kind ?? "Infrastructure"}</span><span className={`${styles.rowMono} ${styles.infraCron}`}>{schedule.scheduleExpression}</span><span className={styles.rowMeta}>{relativeTime(schedule.nextFireTime)}</span></div>
          </li>
        )) : <MessageRow>No infrastructure schedules.</MessageRow>}
      </Card>
      {selectedDefinition && <WorkflowRunModal definition={selectedDefinition} onClose={() => setSelectedDefinition(null)} />}
    </div>
  );
}
